import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './CsCalendar.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const COLLECTION_TITLES = {
  nifty50: 'Nifty - 50',
  fo:      'F&O Stocks',
  cash:    'CASH Stocks',
};
const COLLECTION_TAB_LABELS = {
  nifty50: 'N50',
  fo: 'FO',
  cash: 'CASH',
};
const YEARS = [2024, 2025, 2026];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatNumber(num) {
  const abs = Math.abs(Math.round(num));
  const fmt = new Intl.NumberFormat('en-IN').format(abs);
  return num < 0 ? `-${fmt}` : fmt;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
}

function formatDate(dateStr, day, month, year) {
  if (dateStr) {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return `${day} ${MONTHS_FULL[month - 1]} ${year}`;
}

function fmtVal(v) {
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs >= 1000) return (v < 0 ? '-' : '') + (abs / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(v).toString();
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CsCalendarTest() {
  const [searchParams] = useSearchParams();

  // App state
  const [collection, setCollection] = useState(() => searchParams.get('collection') || 'nifty50');
  const [year, setYear]             = useState(() => parseInt(searchParams.get('year')) || new Date().getFullYear());
  const [month, setMonth]           = useState(() => parseInt(searchParams.get('month')) || new Date().getMonth() + 1);
  const [symbol, setSymbol]         = useState(() => searchParams.get('symbol') || null);
  const [calView, setCalView]       = useState('month');

  // Data state
  const [symbols, setSymbols]           = useState([]);
  const [symbolsData, setSymbolsData]   = useState([]);
  const [symbolIndex, setSymbolIndex]   = useState({}); // { SYMBOL: 'nifty50'|'fo'|'others' } for cross-collection search
  const [calData, setCalData]           = useState(null);
  const [yearData, setYearData]         = useState(null);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [loadingCal, setLoadingCal]         = useState(false);

  // Search & UI
  const [search, setSearch]   = useState('');
  const [tooltip, setTooltip] = useState(null);

  // Sort & copy
  const [sortOrder, setSortOrder]     = useState('desc');
  const [copiedSymbols, setCopiedSymbols] = useState(false);

  // Peak activity filter
  const [peakPeriod, setPeakPeriod]           = useState('monthly');
  const [peakSymbolsData, setPeakSymbolsData] = useState(null);
  const [peakLoading, setPeakLoading]         = useState(false);

  // Spike sub-period (Daily / Weekly), active only when peakPeriod === 'spikes'
  const [spikeSubPeriod, setSpikeSubPeriod]     = useState('daily');
  const [spikeSymbolsData, setSpikeSymbolsData] = useState(null);
  const [spikeLoading, setSpikeLoading]         = useState(false);

  // Peak day mode (stocks whose month-high day is today)
  const [peakDayData, setPeakDayData]       = useState(null);
  const [peakDayLoading, setPeakDayLoading] = useState(false);

  // Refs
  const calGridRef   = useRef(null);
  const stockListRef = useRef(null);
  const tooltipRef   = useRef(null);
  const symbolRef    = useRef(symbol);
  symbolRef.current  = symbol;

  // ── Derived peak-activity data ────────────────────────────────────────────
  const activePeakData = peakPeriod === 'monthly' ? symbolsData : (peakSymbolsData || []);

  const topBuying = useMemo(() =>
    [...activePeakData]
      .filter(s => s.totalCsValue > 0)
      .sort((a, b) => b.totalCsValue - a.totalCsValue)
      .slice(0, 10),
    [activePeakData]
  );

  const topSelling = useMemo(() =>
    [...activePeakData]
      .filter(s => s.totalCsValue < 0)
      .sort((a, b) => a.totalCsValue - b.totalCsValue)
      .slice(0, 10),
    [activePeakData]
  );

  const totalBuying  = useMemo(() => topBuying.reduce((s, x) => s + x.totalCsValue, 0),  [topBuying]);
  const totalSelling = useMemo(() => Math.abs(topSelling.reduce((s, x) => s + x.totalCsValue, 0)), [topSelling]);

  // ── Activity spike detection (daily/weekly vs monthly average) ────────────
  const spikeData = useMemo(() => {
    if (peakPeriod !== 'spikes' || !spikeSymbolsData?.length || !symbolsData.length) {
      return { buying: [], selling: [] };
    }
    const tradingDaysInMonth = Math.max(
      (calData?.dailyData || []).filter(d => d.date).length, 1
    );
    const tradingDaysArr = [...(calData?.dailyData || [])].filter(d => d.date);
    const periodDays = spikeSubPeriod === 'daily' ? 1 : Math.min(5, tradingDaysArr.length || 1);

    const monthlyMap = {};
    symbolsData.forEach(s => { monthlyMap[s.symbol] = s.totalCsValue; });

    const SPIKE_MULT = 1.5;
    const MIN_ABS   = 5;   // ignore noise below ₹5L absolute daily rate
    const spikeBuying  = [];
    const spikeSelling = [];

    spikeSymbolsData.forEach(s => {
      const periodVal = s.totalCsValue;
      if (periodVal === 0) return;
      const monthlyVal = monthlyMap[s.symbol] ?? 0;
      const dailyRate  = periodVal  / periodDays;
      const monthlyAvg = monthlyVal / tradingDaysInMonth;

      if (dailyRate > MIN_ABS) {
        const isReversal = monthlyAvg <= 0;
        const ratio      = isReversal ? 999 : dailyRate / Math.max(monthlyAvg, 0.001);
        if (isReversal || ratio >= SPIKE_MULT) {
          spikeBuying.push({ symbol: s.symbol, totalCsValue: s.totalCsValue, ratio: Math.min(ratio, 99), isReversal });
        }
      } else if (dailyRate < -MIN_ABS) {
        const isReversal = monthlyAvg >= 0;
        const ratio      = isReversal ? 999 : Math.abs(dailyRate) / Math.max(Math.abs(monthlyAvg), 0.001);
        if (isReversal || ratio >= SPIKE_MULT) {
          spikeSelling.push({ symbol: s.symbol, totalCsValue: s.totalCsValue, ratio: Math.min(ratio, 99), isReversal });
        }
      }
    });

    spikeBuying.sort((a, b)  => b.ratio - a.ratio);
    spikeSelling.sort((a, b) => b.ratio - a.ratio);
    return { buying: spikeBuying.slice(0, 10), selling: spikeSelling.slice(0, 10) };
  }, [peakPeriod, spikeSubPeriod, spikeSymbolsData, symbolsData, calData]);

  // ── Peak day: stocks whose highest single day this month is today ──────────
  const peakData = useMemo(() => {
    if (peakPeriod !== 'peak' || !peakDayData) return { buying: [], selling: [] };
    return peakDayData;
  }, [peakPeriod, peakDayData]);

  // ── Load symbols ──────────────────────────────────────────────────────────
  const loadSymbols = useCallback(async () => {
    setLoadingSymbols(true);
    try {
      const { data } = await api.get(
        `/market/symbols/${collection}?year=${year}&month=${month}`
      );
      setSymbols(data.symbols || []);
      setSymbolsData(data.symbolsData || []);
    } catch {
      setSymbols([]);
      setSymbolsData([]);
    } finally {
      setLoadingSymbols(false);
    }
  }, [collection, year, month]);

  // ── Load calendar data (month view) ──────────────────────────────────────
  const loadCalendarData = useCallback(async () => {
    setLoadingCal(true);
    setCalData(null);
    try {
      let url;
      if (symbol) {
        url = `/market/calendar/${collection}?year=${year}&month=${month}&symbol=${encodeURIComponent(symbol)}`;
      } else {
        url = `/market/calendar/${collection}/aggregate?year=${year}&month=${month}`;
      }
      const { data } = await api.get(url);
      if (!data.error) setCalData(data);
    } catch {
      setCalData(null);
    } finally {
      setLoadingCal(false);
    }
  }, [collection, year, month, symbol]);

  // ── Load year view data ───────────────────────────────────────────────────
  const loadYearData = useCallback(async () => {
    setLoadingCal(true);
    setYearData(null);
    try {
      let url = `/market/calendar/${collection}/year-view?year=${year}`;
      if (symbol) url += `&symbol=${encodeURIComponent(symbol)}`;
      const { data } = await api.get(url);
      if (!data.error) setYearData(data);
    } catch {
      setYearData(null);
    } finally {
      setLoadingCal(false);
    }
  }, [collection, year, symbol]);

  // ── Fetch daily / weekly peak data ──────────────────────────────────────
  const fetchPeakData = useCallback(async (period) => {
    if (period === 'monthly') { setPeakSymbolsData(null); return; }
    if (!calData?.dailyData?.length) return;

    const tradingDays = [...calData.dailyData]
      .filter(d => d.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (!tradingDays.length) return;

    let startDate, endDate;
    if (period === 'daily') {
      const d = new Date(tradingDays[0].date);
      startDate = new Date(d); startDate.setUTCHours(0, 0, 0, 0);
      endDate   = new Date(d); endDate.setUTCHours(23, 59, 59, 999);
    } else {
      const last  = new Date(tradingDays[0].date);
      const fifth = new Date(tradingDays[Math.min(4, tradingDays.length - 1)].date);
      startDate = new Date(fifth); startDate.setUTCHours(0, 0, 0, 0);
      endDate   = new Date(last);  endDate.setUTCHours(23, 59, 59, 999);
    }

    setPeakLoading(true);
    try {
      const { data } = await api.get(
        `/market/${collection}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=5000`
      );
      const agg = {};
      (data.data || []).forEach(r => {
        const sym = r.symbol;
        const val = r.csValue?.$numberDecimal
          ? parseFloat(r.csValue.$numberDecimal)
          : parseFloat(r.csValue || 0);
        if (!agg[sym]) agg[sym] = { symbol: sym, totalCsValue: 0 };
        agg[sym].totalCsValue += val;
      });
      setPeakSymbolsData(Object.values(agg));
    } catch (err) {
      console.error('fetchPeakData error:', err);
      setPeakSymbolsData([]);
    } finally {
      setPeakLoading(false);
    }
  }, [calData, collection]);

  // ── Fetch full month data, find stocks whose peak day is the last trading day ─
  const fetchPeakDayData = useCallback(async () => {
    if (!calData?.dailyData?.length) return;
    const tradingDays = [...calData.dailyData]
      .filter(d => d.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (!tradingDays.length) return;

    const lastDay     = new Date(tradingDays[0].date);
    const lastDateStr = lastDay.toISOString().split('T')[0];
    const startDate   = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
    const endDate     = new Date(lastDay); endDate.setUTCHours(23, 59, 59, 999);

    setPeakDayLoading(true);
    try {
      const { data } = await api.get(
        `/market/${collection}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=50000`
      );

      // Build: symDayMap[symbol][YYYY-MM-DD] = total csValue for that day
      const symDayMap = {};
      (data.data || []).forEach(r => {
        const sym     = r.symbol;
        const val     = r.csValue?.$numberDecimal
          ? parseFloat(r.csValue.$numberDecimal)
          : parseFloat(r.csValue || 0);
        const dateStr = r.date ? new Date(r.date).toISOString().split('T')[0] : '';
        if (!dateStr || val === 0) return;
        if (!symDayMap[sym]) symDayMap[sym] = {};
        symDayMap[sym][dateStr] = (symDayMap[sym][dateStr] || 0) + val;
      });

      const peakBuying  = [];
      const peakSelling = [];

      Object.entries(symDayMap).forEach(([sym, dayMap]) => {
        let maxBuyVal = 0, maxBuyDate = '';
        let maxSellVal = 0, maxSellDate = '';
        Object.entries(dayMap).forEach(([date, val]) => {
          if (val > maxBuyVal)  { maxBuyVal  = val; maxBuyDate  = date; }
          if (val < maxSellVal) { maxSellVal = val; maxSellDate = date; }
        });
        if (maxBuyDate  === lastDateStr && maxBuyVal  > 0) peakBuying.push({ symbol: sym, totalCsValue: maxBuyVal });
        if (maxSellDate === lastDateStr && maxSellVal < 0) peakSelling.push({ symbol: sym, totalCsValue: maxSellVal });
      });

      peakBuying.sort((a, b)  => b.totalCsValue - a.totalCsValue);
      peakSelling.sort((a, b) => a.totalCsValue - b.totalCsValue);
      setPeakDayData({ buying: peakBuying.slice(0, 10), selling: peakSelling.slice(0, 10) });
    } catch (err) {
      console.error('fetchPeakDayData error:', err);
      setPeakDayData({ buying: [], selling: [] });
    } finally {
      setPeakDayLoading(false);
    }
  }, [calData, collection, year, month]);

  // ── Effects ─────────────────────────────────────────────────────────────
  useEffect(() => { loadSymbols(); }, [loadSymbols]);

  // Cross-collection symbol index (for unified search)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/market/symbols/index?year=${year}&month=${month}`);
        if (!cancelled) setSymbolIndex(data.index || {});
      } catch {
        if (!cancelled) setSymbolIndex({});
      }
    })();
    return () => { cancelled = true; };
  }, [year, month]);

  useEffect(() => {
    if (calView === 'month') loadCalendarData();
    else loadYearData();
  }, [calView, loadCalendarData, loadYearData]);

  useEffect(() => {
    if (peakPeriod !== 'monthly' && peakPeriod !== 'spikes' && peakPeriod !== 'peak') fetchPeakData(peakPeriod);
    else setPeakSymbolsData(null);
  }, [peakPeriod, calData, fetchPeakData]);

  // ── Fetch spike data (active when peakPeriod === 'spikes') ─────────────────
  const fetchSpikeData = useCallback(async (period) => {
    if (period === 'monthly') { setSpikeSymbolsData(null); return; }
    if (!calData?.dailyData?.length) return;
    const tradingDays = [...calData.dailyData]
      .filter(d => d.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (!tradingDays.length) return;
    let startDate, endDate;
    if (period === 'daily') {
      const d = new Date(tradingDays[0].date);
      startDate = new Date(d); startDate.setUTCHours(0, 0, 0, 0);
      endDate   = new Date(d); endDate.setUTCHours(23, 59, 59, 999);
    } else {
      const last  = new Date(tradingDays[0].date);
      const fifth = new Date(tradingDays[Math.min(4, tradingDays.length - 1)].date);
      startDate = new Date(fifth); startDate.setUTCHours(0, 0, 0, 0);
      endDate   = new Date(last);  endDate.setUTCHours(23, 59, 59, 999);
    }
    setSpikeLoading(true);
    try {
      const { data } = await api.get(
        `/market/${collection}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=5000`
      );
      const agg = {};
      (data.data || []).forEach(r => {
        const sym = r.symbol;
        const val = r.csValue?.$numberDecimal
          ? parseFloat(r.csValue.$numberDecimal)
          : parseFloat(r.csValue || 0);
        if (!agg[sym]) agg[sym] = { symbol: sym, totalCsValue: 0 };
        agg[sym].totalCsValue += val;
      });
      setSpikeSymbolsData(Object.values(agg));
    } catch (err) {
      console.error('fetchSpikeData error:', err);
      setSpikeSymbolsData([]);
    } finally {
      setSpikeLoading(false);
    }
  }, [calData, collection]);

  useEffect(() => {
    if (peakPeriod === 'spikes') fetchSpikeData(spikeSubPeriod);
    else setSpikeSymbolsData(null);
  }, [peakPeriod, spikeSubPeriod, calData, fetchSpikeData]);

  useEffect(() => {
    if (peakPeriod === 'peak') fetchPeakDayData();
    else setPeakDayData(null);
  }, [peakPeriod, calData, fetchPeakDayData]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const valueMap = {};
  symbolsData.forEach(s => { valueMap[s.symbol] = s.totalCsValue; });

  const filteredSymbols = (() => {
    const q = search.trim().toUpperCase();
    let result;

    if (!q) {
      result = symbols.map(sym => ({ symbol: sym, collection, crossCategory: false }));
    } else {
      const seen = new Set();
      result = [];
      for (const sym of symbols) {
        if (!sym.toUpperCase().includes(q)) continue;
        seen.add(sym);
        result.push({ symbol: sym, collection, crossCategory: false });
      }
      for (const sym of Object.keys(symbolIndex)) {
        if (seen.has(sym) || !sym.includes(q)) continue;
        const col = symbolIndex[sym];
        if (col === collection) continue;
        result.push({ symbol: sym, collection: col, crossCategory: true });
      }
    }

    const valOf = (s) => (s.crossCategory ? 0 : (valueMap[s.symbol] || 0));
    if (sortOrder === 'desc')       result = [...result].sort((a, b) => valOf(b) - valOf(a));
    else if (sortOrder === 'asc')   result = [...result].sort((a, b) => valOf(a) - valOf(b));
    else if (sortOrder === 'alpha') result = [...result].sort((a, b) => a.symbol.localeCompare(b.symbol));
    return result;
  })();

  const summary = (() => {
    if (!calData) return { totalValue: 0, totalQty: 0, highest: null, lowest: null, avgValue: 0 };
    if (calView === 'year' && yearData) {
      const monthsWithData = (yearData.months || []).filter(m => m.tradingDays > 0);
      const avgValue = monthsWithData.length ? Math.round((yearData.totalValue || 0) / monthsWithData.length) : 0;
      return {
        totalValue: yearData.totalValue || 0,
        totalQty:   yearData.totalQty   || 0,
        avgValue,
        highest: yearData.highestMonth
          ? { value: yearData.highestMonth.totalValue, label: MONTHS_SHORT[yearData.highestMonth.month - 1] + ' ' + year, today: false }
          : null,
        lowest: yearData.lowestMonth
          ? { value: yearData.lowestMonth.totalValue, label: MONTHS_SHORT[yearData.lowestMonth.month - 1] + ' ' + year, today: false }
          : null,
      };
    }
    const daysWithData = (calData.dailyData || []).filter(d => d.csValue !== undefined && d.csValue !== 0);
    const avgValue = daysWithData.length ? Math.round((calData.totalValue || 0) / daysWithData.length) : 0;
    return {
      totalValue: calData.totalValue || 0,
      totalQty:   calData.totalQty   || 0,
      avgValue,
      highest: calData.highestDay
        ? { value: calData.highestDay.csValue, label: formatDate(calData.highestDay.date, calData.highestDay.day, month, year), today: isToday(calData.highestDay.date) }
        : null,
      lowest: calData.lowestDay
        ? { value: calData.lowestDay.csValue, label: formatDate(calData.lowestDay.date, calData.lowestDay.day, month, year), today: isToday(calData.lowestDay.date) }
        : null,
    };
  })();

  const selectedCollectionLabel = COLLECTION_TAB_LABELS[collection] || 'ALL STOCKS';

  // ── Calendar grid builder ─────────────────────────────────────────────────
  function buildMonthGrid(data) {
    const isAggregate = !symbol;
    const firstDay    = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;

    const dataMap = {};
    (data?.dailyData || []).forEach(d => { dataMap[d.day] = d; });

    const allDays    = Object.values(dataMap).filter(d => d.csValue !== undefined && d.csValue !== 0);
    const highestDay = allDays.length ? allDays.reduce((a, b) => b.csValue > a.csValue ? b : a) : null;
    const lowestDay  = allDays.length ? allDays.reduce((a, b) => b.csValue < a.csValue ? b : a) : null;

    const headers = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
      <div key={d} className="csc-cal-header">{d}</div>
    ));

    const empties = Array.from({ length: startOffset }, (_, i) => (
      <div key={`e${i}`} className="csc-cal-cell empty" />
    ));

    const cells = Array.from({ length: daysInMonth }, (_, i) => {
      const day       = i + 1;
      const dayData   = dataMap[day];
      const hasData   = dayData && dayData.csValue !== undefined && dayData.csValue !== 0;
      const isPos     = hasData && dayData.csValue >= 0;
      const isHighest = hasData && highestDay && dayData.day === highestDay.day;
      const isLowest  = hasData && lowestDay  && dayData.day === lowestDay.day;

      return (
        <div
          key={day}
          className={[
            'csc-cal-cell',
            hasData  ? 'has-data'                        : '',
            hasData  ? (isPos ? 'positive' : 'negative') : '',
            isHighest ? 'peak-high' : '',
            isLowest  ? 'peak-low'  : '',
          ].filter(Boolean).join(' ')}
          onMouseEnter={hasData ? (e) => handleCellEnter(e, day, dayData, isAggregate) : undefined}
          onMouseMove={hasData  ? (e) => handleCellMove(e)  : undefined}
          onMouseLeave={hasData ? handleCellLeave : undefined}
        >
          <span className="csc-cell-day">{day}</span>
          {hasData && <div className="csc-cell-value">{formatNumber(dayData.csValue)}</div>}
          {isHighest && <span className="csc-peak-badge high">▲</span>}
          {isLowest  && <span className="csc-peak-badge low">▼</span>}
        </div>
      );
    });

    const total    = startOffset + daysInMonth;
    const trailing = total % 7 === 0 ? 0 : 7 - (total % 7);
    const tails    = Array.from({ length: trailing }, (_, i) => (
      <div key={`t${i}`} className="csc-cal-cell empty" />
    ));

    return [...headers, ...empties, ...cells, ...tails];
  }

  // ── Year grid builder ─────────────────────────────────────────────────────
  function buildYearGrid(data) {
    if (!data?.months) return null;
    const maxAbs = Math.max(...data.months.map(m => Math.abs(m.totalValue)), 1);

    return data.months.map(m => {
      const hasData = m.tradingDays > 0;
      const isPos   = m.totalValue >= 0;
      const cls     = hasData ? (isPos ? 'positive' : 'negative') : '';
      const pct     = hasData ? Math.round((Math.abs(m.totalValue) / maxAbs) * 100) : 0;

      return (
        <div
          key={m.month}
          className={`csc-ymc ${cls}${!hasData ? ' no-data' : ''}`}
          onClick={hasData ? () => drillDown(m.month) : undefined}
          title={hasData ? `Click to view ${MONTHS_SHORT[m.month - 1]} in Month View` : 'No data'}
        >
          <div>
            <div className="csc-ymc-name">{MONTHS_SHORT[m.month - 1]}</div>
            <div className={`csc-ymc-value ${cls}`}>
              {hasData ? formatNumber(m.totalValue) : '—'}
            </div>
            <div className="csc-ymc-meta">
              <span>{hasData ? `${m.tradingDays} days` : 'No data'}</span>
              {data.isAggregate && hasData && <span>{m.symbolCount} stocks</span>}
            </div>
            <div className="csc-ymc-bar">
              <div className={`csc-ymc-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      );
    });
  }

  function drillDown(m) { setMonth(m); setCalView('month'); }

  // ── Tooltip handlers ──────────────────────────────────────────────────────
  function handleCellEnter(e, day, dayData, isAggregate) {
    positionTooltip(e.clientX, e.clientY, day, dayData, isAggregate);
  }
  function handleCellMove(e) {
    if (!tooltip) return;
    const { x, y } = getTooltipPosition(e.clientX, e.clientY);
    setTooltip(prev => prev ? { ...prev, x, y } : prev);
  }
  function handleCellLeave() { setTooltip(null); }

  function getTooltipPosition(mx, my) {
    const margin = 8;
    const offset = 12;
    const tw = tooltipRef.current?.offsetWidth || Math.min(240, window.innerWidth - margin * 2);
    const th = tooltipRef.current?.offsetHeight || 170;
    const maxX = Math.max(margin, window.innerWidth - tw - margin);
    const maxY = Math.max(margin, window.innerHeight - th - margin);

    let x = mx + offset;
    let y = my + offset;
    if (x + tw + margin > window.innerWidth) x = mx - tw - offset;
    if (y + th + margin > window.innerHeight) y = my - th - offset;

    return {
      x: Math.min(Math.max(x, margin), maxX),
      y: Math.min(Math.max(y, margin), maxY),
    };
  }

  function positionTooltip(mx, my, day, dayData, isAggregate) {
    const { x, y } = getTooltipPosition(mx, my);

    setTooltip({
      x, y, day,
      value:       dayData.csValue,
      qty:         dayData.csQty || 0,
      price:       dayData.avgIEP || 0,
      date:        dayData.date || '',
      symbolCount: dayData.symbolCount || 0,
      isAggregate,
    });
  }

  // ── Keyboard navigation ───────────────────────────────────────────────────
  const navigateSymbol = useCallback((dir) => {
    if (!filteredSymbols.length) return;
    let idx = filteredSymbols.findIndex(s => s.symbol === symbolRef.current);
    if (idx === -1) idx = dir === 1 ? 0 : filteredSymbols.length - 1;
    else {
      idx += dir;
      if (idx < 0) idx = filteredSymbols.length - 1;
      if (idx >= filteredSymbols.length) idx = 0;
    }
    const next = filteredSymbols[idx];
    if (next.collection !== collection) {
      setCollection(next.collection);
      setSymbol(next.symbol);
      return;
    }
    setSymbol(next.symbol);
    setTimeout(() => {
      const el = stockListRef.current?.querySelector(`[data-symbol="${next.symbol}"]`);
      if (el) {
        const list = stockListRef.current;
        const elTop = el.offsetTop;
        const elBot = elTop + el.offsetHeight;
        const visTop = list.scrollTop;
        const visBot = visTop + list.clientHeight;
        if (elTop < visTop) list.scrollTop = elTop - 4;
        else if (elBot > visBot) list.scrollTop = elBot - list.clientHeight + 4;
      }
    }, 0);
  }, [filteredSymbols, collection]);

  const navPosText = () => {
    if (!symbol || !filteredSymbols.length) return '';
    const idx = filteredSymbols.findIndex(s => s.symbol === symbol);
    return idx !== -1 ? `${idx + 1}/${filteredSymbols.length}` : '';
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown')      { e.preventDefault(); navigateSymbol(+1); }
      else if (e.key === 'ArrowUp')   { e.preventDefault(); navigateSymbol(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigateSymbol]);

  function handleCollectionChange(col) { setCollection(col); setSymbol(null); setSearch(''); }
  function handleSymbolClick(sym, targetCollection) {
    const col = targetCollection || collection;
    if (col !== collection) {
      setCollection(col);
      setSymbol(sym);
      return;
    }
    setSymbol(sym);
  }

  function getAdjacentMonth(offset) {
    const target = new Date(Date.UTC(year, month - 1 + offset, 1));
    return {
      year: target.getUTCFullYear(),
      month: target.getUTCMonth() + 1,
      label: `${MONTHS_SHORT[target.getUTCMonth()]} ${target.getUTCFullYear()}`,
    };
  }

  function navigateCalendarMonth(offset) {
    const target = getAdjacentMonth(offset);
    setYear(target.year);
    setMonth(target.month);
    setCalView('month');
  }

  const copyPositiveSymbols = () => {
    const positives = symbolsData.filter(s => s.totalCsValue > 0);
    if (!positives.length) return;
    const text = positives.map(s => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedSymbols(true);
    setTimeout(() => setCopiedSymbols(false), 2000);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="CS Calendar">
      <div className="csc-root">

        {/* ── Left Stock Panel ───────────────────────────────────────── */}
        <aside className="csc-stock-panel csct-panel">
          <div className="csc-collection-tabs">
            {(['nifty50','fo','cash']).map(col => (
              <button
                key={col}
                className={`csc-col-tab${collection === col ? ' active' : ''}`}
                onClick={() => handleCollectionChange(col)}
              >
                {COLLECTION_TAB_LABELS[col]}
              </button>
            ))}
          </div>

          <div className="csc-search">
            <input
              type="text"
              placeholder="Search symbol..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Sort & Copy row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {[{ key: 'desc', label: '↓ CS' }, { key: 'asc', label: '↑ CS' }, { key: 'alpha', label: 'A–Z' }].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSortOrder(opt.key)}
                  style={{
                    fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                    borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                    border: sortOrder === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                    background: sortOrder === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                    color: sortOrder === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={copyPositiveSymbols}
              disabled={!symbolsData.filter(s => s.totalCsValue > 0).length}
              title="Copy all positive symbols for TradingView watchlist"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: '0.68rem', fontWeight: 600,
                padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                border: copiedSymbols ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                background: copiedSymbols ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                color: copiedSymbols ? '#6ee7b7' : '#10b981',
                transition: 'all 0.2s',
                opacity: symbolsData.filter(s => s.totalCsValue > 0).length ? 1 : 0.4,
              }}
            >
              {copiedSymbols ? '✓ Copied!' : '⧉ Copy'}
            </button>
          </div>

          <div className="csc-stock-list" ref={stockListRef}>
            {loadingSymbols ? (
              <div className="csc-loading"><div className="csc-spinner" />Loading...</div>
            ) : filteredSymbols.length === 0 ? (
              <div className="csc-no-data">No symbols found</div>
            ) : (
              filteredSymbols.map(item => {
                const sym = item.symbol;
                const v   = item.crossCategory ? 0 : (valueMap[sym] || 0);
                const cls = item.crossCategory ? 'neutral' : (v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral');
                return (
                  <div
                    key={`${item.collection}:${sym}`}
                    data-symbol={sym}
                    className={`csc-stock-item ${cls}${symbol === sym && item.collection === collection ? ' active' : ''}`}
                    onClick={() => handleSymbolClick(sym, item.collection)}
                  >
                    <span className="csct-item-sym" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {sym}
                      {item.crossCategory && (
                        <span
                          className="csc-col-tab active"
                          style={{ fontSize: '0.55rem', padding: '1px 5px', pointerEvents: 'none' }}
                          title={`In ${COLLECTION_TAB_LABELS[item.collection]} — click to switch`}
                        >
                          {COLLECTION_TAB_LABELS[item.collection]}
                        </span>
                      )}
                    </span>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 600, flexShrink: 0,
                      color: item.crossCategory
                        ? 'rgba(255,255,255,0.4)'
                        : v > 0 ? '#4ade80' : v < 0 ? '#f87171' : 'rgba(255,255,255,0.4)',
                      minWidth: 36, textAlign: 'right',
                    }}>{item.crossCategory ? '—' : fmtVal(v)}</span>
                    <a
                      href={`https://in.tradingview.com/chart/?symbol=NSE%3A${sym}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View ${sym} on TradingView`}
                      className="csct-tv-link"
                      onClick={e => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                    </a>
                  </div>
                );
              })
            )}
          </div>

          <div className="csc-nav-bar">
            <button className="csc-nav-key" onClick={() => navigateSymbol(-1)} title="Previous (↑)">▲</button>
            <button className="csc-nav-key" onClick={() => navigateSymbol(+1)} title="Next (↓)">▼</button>
            <span className="csc-nav-label">navigate</span>
            <span className="csc-nav-pos">{navPosText()}</span>
          </div>
        </aside>

        {/* ── Main Content ───────────────────────────────────────────── */}
        <main className="csc-main">
          <div className="csc-filter-row">
            {/* Selectors row */}
            <div className="csc-selectors">
            <div className="csc-selector-group">
              <div className="csc-view-toggle">
                <button
                  className={`csc-view-btn${calView === 'month' ? ' active' : ''}`}
                  onClick={() => setCalView('month')}
                >
                  📅 Month
                </button>
                <button
                  className={`csc-view-btn${calView === 'year' ? ' active' : ''}`}
                  onClick={() => setCalView('year')}
                >
                  📆 Year
                </button>
              </div>
            </div>

            <div className="csc-selector-group">
              <span className="csc-selector-label">Year:</span>
              <select
                className="csc-selector-select"
                aria-label="Year"
                value={year}
                onChange={(event) => setYear(parseInt(event.target.value, 10))}
              >
                {[...new Set([...YEARS, year])].sort((a, b) => a - b).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {calView === 'month' && (
              <div className="csc-selector-group">
                <span className="csc-selector-label">Month:</span>
                <select
                  className="csc-selector-select"
                  aria-label="Month"
                  value={month}
                  onChange={(event) => setMonth(parseInt(event.target.value, 10))}
                >
                  {MONTHS_SHORT.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
            )}
            </div>

            <div className="csc-collection-label">{COLLECTION_TITLES[collection]}</div>
          </div>

          {/* Summary cards */}
          <div className="csc-summary">
            <div className="csc-card">
              <div className="csc-card-label">Value</div>
              <div className={`csc-card-value ${summary.totalValue >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(summary.totalValue)}
              </div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Qty</div>
              <div className="csc-card-value">{formatNumber(summary.totalQty)}</div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Highest</div>
              <div className={`csc-card-value ${summary.highest ? (summary.highest.value >= 0 ? 'positive' : 'negative') : 'positive'}`}>
                {summary.highest ? formatNumber(summary.highest.value) : '—'}
              </div>
              <div className={`csc-card-date${summary.highest?.today ? ' today' : ''}`}>
                {summary.highest?.label || '-'}
              </div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Lowest</div>
              <div className={`csc-card-value ${summary.lowest ? (summary.lowest.value >= 0 ? 'positive' : 'negative') : 'negative'}`}>
                {summary.lowest ? formatNumber(summary.lowest.value) : '—'}
              </div>
              <div className={`csc-card-date${summary.lowest?.today ? ' today' : ''}`}>
                {summary.lowest?.label || '-'}
              </div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Avg</div>
              <div className={`csc-card-value ${summary.avgValue >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(summary.avgValue)}
              </div>
              <div className="csc-card-date">
                {calView === 'year' ? 'per month' : 'per day'}
              </div>
            </div>
          </div>

          {/* ── Calendar section ───────────────────────────────────── */}
          <div className="csc-calendar-section">
            <div className="csc-cal-title">
              <span>CS Value</span>
              <span className="csc-cal-context">{symbol || selectedCollectionLabel}</span>
              {calView === 'year' && <span className="csc-cal-month">{year}</span>}
            </div>

            {/* ── MONTH VIEW: grid + peak panel side-by-side ─────── */}
            {calView === 'month' && (
              <div className="csct-row">

                {/* Reduced-width calendar grid */}
                <div className="csct-grid-wrap">
                  <div className="csc-month-nav" aria-label="Calendar month navigation">
                    <button type="button" onClick={() => navigateCalendarMonth(-1)}>
                      ‹ {getAdjacentMonth(-1).label}
                    </button>
                    <span className="csc-cal-month">{MONTHS_FULL[month - 1]} {year}</span>
                    <button type="button" onClick={() => navigateCalendarMonth(1)}>
                      {getAdjacentMonth(1).label} ›
                    </button>
                  </div>
                  <div className="csc-cal-grid" ref={calGridRef}>
                    {loadingCal ? (
                      <div className="csc-loading">
                        <div className="csc-spinner" />Loading calendar...
                      </div>
                    ) : !calData ? (
                      <div className="csc-no-data">No data available</div>
                    ) : (
                      buildMonthGrid(calData)
                    )}
                  </div>
                </div>

                {/* ── Peak Activity panel ─────────────────────────── */}
                <div className="csct-peak-panel">

                  {/* Panel header */}
                  <div className="csct-peak-header">
                    {/* Title + filter buttons on one row */}
                    <div className="csct-peak-header-top">
                      <div className="csct-peak-header-title" style={{ marginBottom: 0 }}>
                        <div className="csct-peak-icon-wrap">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="10" width="4" height="11" rx="1" fill="#667eea"/>
                            <rect x="8" y="6" width="4" height="15" rx="1" fill="#764ba2"/>
                            <rect x="14" y="2" width="4" height="19" rx="1" fill="#667eea"/>
                            <rect x="20" y="7" width="4" height="14" rx="1" fill="#764ba2"/>
                          </svg>
                        </div>
                        <span className="csct-peak-title-text">CS Activity Radar</span>
                      </div>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {[
                          { key: 'daily',   label: 'Daily' },
                          { key: 'weekly',  label: 'Weekly' },
                          { key: 'monthly', label: 'Monthly' },
                          { key: 'spikes',  label: '⚡ Spikes' },
                          { key: 'peak',    label: '🏔 Peak' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setPeakPeriod(opt.key)}
                            style={{
                              fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px',
                              borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                              border: peakPeriod === opt.key
                                ? (opt.key === 'spikes' ? '1px solid rgba(245,158,11,0.8)' : opt.key === 'peak' ? '1px solid rgba(20,184,166,0.8)' : '1px solid rgba(139,92,246,0.8)')
                                : '1px solid rgba(255,255,255,0.15)',
                              background: peakPeriod === opt.key
                                ? (opt.key === 'spikes' ? 'rgba(245,158,11,0.3)' : opt.key === 'peak' ? 'rgba(20,184,166,0.3)' : 'rgba(139,92,246,0.3)')
                                : 'rgba(255,255,255,0.05)',
                              color: peakPeriod === opt.key
                                ? (opt.key === 'spikes' ? '#fcd34d' : opt.key === 'peak' ? '#5eead4' : '#c4b5fd')
                                : 'rgba(255,255,255,0.4)',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Sub-period toggle — shown only in Spikes mode */}
                    {peakPeriod === 'spikes' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0 2px', borderTop: '1px solid rgba(245,158,11,0.15)', marginTop: 4 }}>
                        <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', marginRight: 2 }}>Compare:</span>
                        {[{ key: 'daily', label: 'Today' }, { key: 'weekly', label: 'This Week' }].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setSpikeSubPeriod(opt.key)}
                            style={{
                              fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px',
                              borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                              border: spikeSubPeriod === opt.key ? '1px solid rgba(245,158,11,0.7)' : '1px solid rgba(255,255,255,0.1)',
                              background: spikeSubPeriod === opt.key ? 'rgba(245,158,11,0.2)' : 'transparent',
                              color: spikeSubPeriod === opt.key ? '#fcd34d' : 'rgba(255,255,255,0.35)',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                        <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.25)', marginLeft: 2, fontStyle: 'italic' }}>vs {MONTHS_FULL[month-1]} avg</span>
                      </div>
                    )}
                  </div>

                  {/* Buying section */}
                  <div className="csct-section csct-buying">
                    <div className="csct-section-header">
                      <div className="csct-section-icon buying">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                      </div>
                      <div className="csct-section-meta">
                        <span className="csct-section-label">
                          {peakPeriod === 'spikes'  ? '↑ SURGE BUYING'
                            : peakPeriod === 'peak'   ? '↑ PEAK BUYING TODAY'
                            : peakPeriod === 'daily'  ? "TODAY'S HIGHEST BUYING"
                            : peakPeriod === 'weekly' ? "WEEK'S HIGHEST BUYING"
                            : "MONTH'S HIGHEST BUYING"}
                        </span>
                        <span className="csct-section-amount buying">
                          ₹{formatNumber(
                            peakPeriod === 'spikes'
                              ? spikeData.buying.reduce((s, x) => s + x.totalCsValue, 0)
                              : peakPeriod === 'peak'
                                ? peakData.buying.reduce((s, x) => s + x.totalCsValue, 0)
                                : totalBuying
                          )} L
                        </span>
                      </div>
                    </div>
                    <div className="csct-chips">
                      {(loadingSymbols || (peakPeriod === 'spikes' ? spikeLoading : peakPeriod === 'peak' ? peakDayLoading : peakLoading)) ? (
                        <span className="csct-no-data">Loading…</span>
                      ) : peakPeriod === 'spikes' ? (
                        spikeData.buying.length === 0
                          ? <span className="csct-no-data">No surge buying detected</span>
                          : spikeData.buying.map(s => (
                            <span
                              key={s.symbol}
                              className={`csct-chip${symbol === s.symbol ? ' selected' : ''}`}
                              style={{
                                background: s.isReversal ? 'rgba(167,243,208,0.12)' : 'rgba(245,158,11,0.18)',
                                border: `1px solid ${s.isReversal ? 'rgba(52,211,153,0.5)' : 'rgba(245,158,11,0.5)'}`,
                                color: s.isReversal ? '#6ee7b7' : '#fcd34d',
                              }}
                              title={`${s.isReversal ? 'REVERSAL — was net selling this month' : `${s.ratio.toFixed(1)}× monthly avg`} · CS: ${formatNumber(s.totalCsValue)}`}
                              onClick={() => setSymbol(s.symbol)}
                            >
                              {s.symbol}
                              {s.isReversal
                                ? <span style={{ marginLeft: 2, fontSize: '0.5rem', opacity: 0.8 }}>↺</span>
                                : <span style={{ marginLeft: 2, fontSize: '0.5rem', opacity: 0.7 }}>{s.ratio.toFixed(1)}×</span>}
                            </span>
                          ))
                      ) : peakPeriod === 'peak' ? (
                        peakData.buying.length === 0
                          ? <span className="csct-no-data">No peak buying today</span>
                          : peakData.buying.map(s => (
                            <span
                              key={s.symbol}
                              className={`csct-chip${symbol === s.symbol ? ' selected' : ''}`}
                              style={{ background: 'rgba(20,184,166,0.18)', border: '1px solid rgba(20,184,166,0.5)', color: '#5eead4' }}
                              title={`Month's highest buying day · CS: ${formatNumber(s.totalCsValue)}`}
                              onClick={() => setSymbol(s.symbol)}
                            >
                              {s.symbol}
                            </span>
                          ))
                      ) : topBuying.length === 0 ? (
                        <span className="csct-no-data">No data</span>
                      ) : (
                        topBuying.map(s => (
                          <span
                            key={s.symbol}
                            className={`csct-chip buying${symbol === s.symbol ? ' selected' : ''}`}
                            title={`CS Value: ${formatNumber(s.totalCsValue)} · Click to view calendar`}
                            onClick={() => setSymbol(s.symbol)}
                          >
                            {s.symbol}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Selling section */}
                  <div className="csct-section csct-selling">
                    <div className="csct-section-header">
                      <div className="csct-section-icon selling">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
                          <polyline points="17 18 23 18 23 12"/>
                        </svg>
                      </div>
                      <div className="csct-section-meta">
                        <span className="csct-section-label">
                          {peakPeriod === 'spikes'  ? '↓ SURGE SELLING'
                            : peakPeriod === 'peak'   ? '↓ PEAK SELLING TODAY'
                            : peakPeriod === 'daily'  ? "TODAY'S HIGHEST SELLING"
                            : peakPeriod === 'weekly' ? "WEEK'S HIGHEST SELLING"
                            : "MONTH'S HIGHEST SELLING"}
                        </span>
                        <span className="csct-section-amount selling">
                          ₹{formatNumber(
                            peakPeriod === 'spikes'
                              ? Math.abs(spikeData.selling.reduce((s, x) => s + x.totalCsValue, 0))
                              : peakPeriod === 'peak'
                                ? Math.abs(peakData.selling.reduce((s, x) => s + x.totalCsValue, 0))
                                : totalSelling
                          )} L
                        </span>
                      </div>
                    </div>
                    <div className="csct-chips">
                      {(loadingSymbols || (peakPeriod === 'spikes' ? spikeLoading : peakPeriod === 'peak' ? peakDayLoading : peakLoading)) ? (
                        <span className="csct-no-data">Loading…</span>
                      ) : peakPeriod === 'spikes' ? (
                        spikeData.selling.length === 0
                          ? <span className="csct-no-data">No surge selling detected</span>
                          : spikeData.selling.map(s => (
                            <span
                              key={s.symbol}
                              className={`csct-chip${symbol === s.symbol ? ' selected' : ''}`}
                              style={{
                                background: s.isReversal ? 'rgba(251,113,133,0.12)' : 'rgba(251,146,60,0.18)',
                                border: `1px solid ${s.isReversal ? 'rgba(251,113,133,0.5)' : 'rgba(251,146,60,0.5)'}`,
                                color: s.isReversal ? '#fca5a5' : '#fb923c',
                              }}
                              title={`${s.isReversal ? 'REVERSAL — was net buying this month' : `${s.ratio.toFixed(1)}× monthly avg`} · CS: ${formatNumber(s.totalCsValue)}`}
                              onClick={() => setSymbol(s.symbol)}
                            >
                              {s.symbol}
                              {s.isReversal
                                ? <span style={{ marginLeft: 2, fontSize: '0.5rem', opacity: 0.8 }}>↺</span>
                                : <span style={{ marginLeft: 2, fontSize: '0.5rem', opacity: 0.7 }}>{s.ratio.toFixed(1)}×</span>}
                            </span>
                          ))
                      ) : peakPeriod === 'peak' ? (
                        peakData.selling.length === 0
                          ? <span className="csct-no-data">No peak selling today</span>
                          : peakData.selling.map(s => (
                            <span
                              key={s.symbol}
                              className={`csct-chip${symbol === s.symbol ? ' selected' : ''}`}
                              style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}
                              title={`Month's highest selling day · CS: ${formatNumber(s.totalCsValue)}`}
                              onClick={() => setSymbol(s.symbol)}
                            >
                              {s.symbol}
                            </span>
                          ))
                      ) : topSelling.length === 0 ? (
                        <span className="csct-no-data">No data</span>
                      ) : (
                        topSelling.map(s => (
                          <span
                            key={s.symbol}
                            className={`csct-chip selling${symbol === s.symbol ? ' selected' : ''}`}
                            title={`CS Value: ${formatNumber(s.totalCsValue)} · Click to view calendar`}
                            onClick={() => setSymbol(s.symbol)}
                          >
                            {s.symbol}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Footer note */}
                  <div className="csct-peak-footer">
                    {peakPeriod === 'spikes'
                      ? `≥1.5× daily avg · ${spikeSubPeriod === 'daily' ? 'Today' : 'Last 5 days'} vs ${MONTHS_FULL[month - 1]} ${year}`
                      : peakPeriod === 'peak'
                        ? `≥1× daily avg · Today vs ${MONTHS_FULL[month - 1]} ${year} avg`
                        : `Top 10 by CS Value · ${peakPeriod === 'daily' ? 'Last trading day' : peakPeriod === 'weekly' ? 'Last 5 trading days' : `${MONTHS_FULL[month - 1]} ${year}`}`}
                  </div>
                </div>
              </div>
            )}

            {/* ── YEAR VIEW ──────────────────────────────────────── */}
            {calView === 'year' && (
              <div className="csc-year-grid">
                {loadingCal ? (
                  <div className="csc-loading" style={{ gridColumn: '1 / -1' }}>
                    <div className="csc-spinner" />Loading year data...
                  </div>
                ) : !yearData ? (
                  <div className="csc-no-data" style={{ gridColumn: '1 / -1' }}>No data available</div>
                ) : (
                  buildYearGrid(yearData)
                )}
              </div>
            )}
          </div>
        </main>

        {/* ── Tooltip ─────────────────────────────────────────────────── */}
        {tooltip && (
          <div
            ref={tooltipRef}
            className="csc-tooltip"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <div className="csc-tt-header">
              {tooltip.isAggregate
                ? `ALL STOCKS (${COLLECTION_TITLES[collection]})`
                : (symbol || 'STOCK')}
            </div>
            <div className="csc-tt-row">
              <span className="csc-tt-label">Date:</span>
              <span className="csc-tt-val">
                {tooltip.date
                  ? new Date(tooltip.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                  : `${tooltip.day} ${MONTHS_FULL[month - 1]} ${year}`}
              </span>
            </div>
            <div className="csc-tt-row">
              <span className="csc-tt-label">
                {tooltip.isAggregate ? 'Stocks:' : 'Price (IEP):'}
              </span>
              <span className="csc-tt-val">
                {tooltip.isAggregate
                  ? `${tooltip.symbolCount} stocks`
                  : (tooltip.price > 0 ? `₹${tooltip.price.toFixed(2)}` : '-')}
              </span>
            </div>
            <div className="csc-tt-row">
              <span className="csc-tt-label">CS Value:</span>
              <span className={`csc-tt-val ${tooltip.value >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(tooltip.value)}
              </span>
            </div>
            <div className="csc-tt-row">
              <span className="csc-tt-label">CS Qty:</span>
              <span className="csc-tt-val">{formatNumber(tooltip.qty)}</span>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
